/**
 * Pacing for the speed reading trainer: turning pasted text into words, chunks of words,
 * and how long each chunk stays on screen at a set words-per-minute.
 *
 * The pace is a target, not a promise about comprehension: faster presentation trades
 * accuracy for speed (Rayner et al., 2016), which the page says as plainly as it can.
 */

export const MIN_WPM = 100;
export const MAX_WPM = 1000;
export const MAX_WORDS = 20_000;

const WHITESPACE = /\s+/;
const SENTENCE_END = /[.!?…]["'”’)\]]*$/;
const CLAUSE_END = /[,;:–—]["'”’)\]]*$/;
const LETTER = /[\p{L}\p{N}]/u;

/** The words of `text`, punctuation kept on the word it touches, capped at `MAX_WORDS`. */
export function tokenize(text: string): string[] {
  const words = text.split(WHITESPACE).filter((word) => word.length > 0);
  return words.slice(0, MAX_WORDS);
}

export function endsSentence(word: string): boolean {
  return SENTENCE_END.test(word);
}

export function endsClause(word: string): boolean {
  return CLAUSE_END.test(word);
}

/**
 * Groups of up to `size` words. With `breakAtPunctuation`, a group also ends at the end
 * of a sentence or clause, so a chunk never straddles a full stop or a comma.
 */
export function chunkWords(
  words: readonly string[],
  size: number,
  breakAtPunctuation: boolean
): string[][] {
  const limit = Math.max(1, Math.floor(size));
  const chunks: string[][] = [];
  let current: string[] = [];
  for (const word of words) {
    current.push(word);
    const atBreak =
      breakAtPunctuation && (endsSentence(word) || endsClause(word));
    if (current.length >= limit || atBreak) {
      chunks.push(current);
      current = [];
    }
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}

/** Extra time after a chunk, as a multiple of its base time. */
const SENTENCE_PAUSE = 0.8;
const CLAUSE_PAUSE = 0.35;

/**
 * How long a chunk stays on screen, in milliseconds: its words at `wpm`, plus a longer
 * pause after a sentence or clause when `punctuationPauses` is on.
 */
export function chunkDelay(
  chunk: readonly string[],
  wpm: number,
  punctuationPauses: boolean
): number {
  const base = (60_000 / Math.max(1, wpm)) * chunk.length;
  const last = chunk.at(-1) ?? "";
  if (!punctuationPauses) {
    return base;
  }
  if (endsSentence(last)) {
    return base * (1 + SENTENCE_PAUSE);
  }
  if (endsClause(last)) {
    return base * (1 + CLAUSE_PAUSE);
  }
  return base;
}

/** Total seconds to show every chunk, and the pace that works out to (words per minute). */
export function estimateRun(
  chunks: readonly (readonly string[])[],
  wpm: number,
  punctuationPauses: boolean
): { seconds: number; effectiveWpm: number; words: number } {
  let milliseconds = 0;
  let words = 0;
  for (const chunk of chunks) {
    milliseconds += chunkDelay(chunk, wpm, punctuationPauses);
    words += chunk.length;
  }
  const seconds = milliseconds / 1000;
  return {
    seconds,
    effectiveWpm: seconds > 0 ? Math.round((words / seconds) * 60) : 0,
    words,
  };
}

export interface Pivot {
  before: string;
  pivot: string;
  after: string;
}

/**
 * The letter a single word is anchored on, so the eye can stay still while words change:
 * the second letter of a short word, and further in for longer ones (the usual rule of
 * thumb in RSVP readers). Leading punctuation is kept in `before` and ignored in the count.
 */
export function splitPivot(word: string): Pivot {
  const characters = Array.from(word);
  const lead = characters.findIndex((character) => LETTER.test(character));
  if (lead === -1) {
    return { before: word, pivot: "", after: "" };
  }
  let trail = characters.length - 1;
  while (trail > lead && !LETTER.test(characters[trail] ?? "")) {
    trail -= 1;
  }
  const length = trail - lead + 1;
  let offset = 4;
  if (length <= 1) {
    offset = 0;
  } else if (length <= 5) {
    offset = 1;
  } else if (length <= 9) {
    offset = 2;
  } else if (length <= 13) {
    offset = 3;
  }
  const at = lead + offset;
  return {
    before: characters.slice(0, at).join(""),
    pivot: characters[at] ?? "",
    after: characters.slice(at + 1).join(""),
  };
}

/** Words per minute from a word count and elapsed milliseconds; 0 when nothing has elapsed. */
export function measuredWpm(words: number, milliseconds: number): number {
  return milliseconds > 0 ? Math.round((words / milliseconds) * 60_000) : 0;
}

/** The index of the chunk that holds word number `wordIndex`. */
export function chunkIndexOfWord(
  chunks: readonly (readonly string[])[],
  wordIndex: number
): number {
  let seen = 0;
  for (let i = 0; i < chunks.length; i += 1) {
    seen += chunks[i]?.length ?? 0;
    if (wordIndex < seen) {
      return i;
    }
  }
  return Math.max(0, chunks.length - 1);
}

/** The number of words before chunk `index`. */
export function wordsBefore(
  chunks: readonly (readonly string[])[],
  index: number
): number {
  let count = 0;
  for (let i = 0; i < Math.min(index, chunks.length); i += 1) {
    count += chunks[i]?.length ?? 0;
  }
  return count;
}
